import { ArrowRightCircleIcon, BellIcon, DocumentCheckIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { Box, Stack, Typography } from '@mui/material'
import { isAIDebriefActivity } from 'pure-js/libs/isAIDebriefActivity'
import { InAppNotificationType } from 'pure-js/enums/InAppNotificationType'
import type { InAppNotification, GameReminderPayload } from 'pure-js/types/InAppNotification'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../constants/colors'

function getTimeAgo(isoDate: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return t('notifications.timeAgo.justNow')
  if (mins < 60) return t('notifications.timeAgo.minutesAgo', { count: mins })
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return t('notifications.timeAgo.hoursAgo', { count: hrs })
  return t('notifications.timeAgo.daysAgo', { count: Math.floor(hrs / 24) })
}

const ICON_MAP = {
  [InAppNotificationType.GAME_REMINDER]: BellIcon,
  [InAppNotificationType.REPORT_READY]: DocumentCheckIcon,
  [InAppNotificationType.FEEDBACK_READY]: SparklesIcon,
  [InAppNotificationType.SENT_TO_ACTIVITY]: ArrowRightCircleIcon
}

export const NotificationHistoryItem = ({
  notification,
  onNavigate,
  isDisabled = false
}: {
  notification: InAppNotification
  onNavigate: (notification: InAppNotification) => void
  isDisabled?: boolean
}) => {
  const { t } = useTranslation()
  const Icon = ICON_MAP[notification.type]
  const isUnread = !notification.viewedAt
  const disabled = isDisabled

  const getTitle = () => {
    if (notification.type === InAppNotificationType.GAME_REMINDER) {
      const p = notification.payload as GameReminderPayload
      if (isAIDebriefActivity(p.activityType)) {
        return t('notifications.aiDebriefReminder.sidebarTitle', { sprintTitle: p.sprintTitle })
      }
      return t('notifications.gameReminder.sidebarTitle', { sprintTitle: p.sprintTitle })
    }
    if (notification.type === InAppNotificationType.REPORT_READY) return t('notifications.reportReady.sidebarTitle')
    return t('notifications.feedbackReady.sidebarTitle')
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={() => !disabled && onNavigate(notification)}
      disabled={disabled}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        width: '100%',
        textAlign: 'left',
        border: 'none',
        borderLeft: isUnread && !disabled ? `3px solid ${INTERAX_COLORS.sun}` : '3px solid transparent',
        background:
          isUnread && !disabled
            ? 'linear-gradient(90deg, rgba(46,79,95,0.35) 0%, rgba(46,79,95,0.12) 100%)'
            : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: '0 8px 8px 0',
        transition: 'background 0.15s ease',
        opacity: disabled ? 0.7 : 1,
        '&:hover': disabled
          ? {}
          : {
              background: isUnread
                ? 'linear-gradient(90deg, rgba(46,79,95,0.5) 0%, rgba(46,79,95,0.25) 100%)'
                : 'rgba(255,255,255,0.05)'
            }
      }}
    >
      {/* Icon circle */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: isUnread && !disabled ? 'rgba(255,171,64,0.15)' : 'rgba(255,255,255,0.06)',
          border: `1px solid ${isUnread && !disabled ? 'rgba(255,171,64,0.35)' : 'rgba(255,255,255,0.08)'}`
        }}
      >
        <Icon
          width={18}
          height={18}
          style={{ color: isUnread && !disabled ? INTERAX_COLORS.sun : INTERAX_COLORS.thunderGrey }}
          aria-hidden
        />
      </Box>

      {/* Text */}
      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Typography
          variant="body2"
          fontWeight={isUnread && !disabled ? 600 : 400}
          noWrap
          sx={{ color: isUnread && !disabled ? INTERAX_COLORS.white : INTERAX_COLORS.white70 }}
        >
          {getTitle()}
        </Typography>
        {notification.type !== InAppNotificationType.GAME_REMINDER && (
          <Typography variant="caption" sx={{ color: INTERAX_COLORS.thunderGrey }}>
            {getTimeAgo(notification.createdAt, t)}
          </Typography>
        )}
      </Stack>

      {/* Unread dot */}
      {isUnread && !disabled && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: INTERAX_COLORS.sun,
            flexShrink: 0
          }}
        />
      )}
    </Box>
  )
}
